import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {BaseRecordTableItemComponent} from "../../base-record-table-item/base-record-table-item.component";
import {MatOption, MatSelect, MatSelectChange} from "@angular/material/select";
import {LoadingService} from "../../../../../services/loading.service";
import {UtilityService} from "../../../../../services/utility.service";
import {SelectRecordTableItemService} from "./services/select-record-table-item.service";
import {SelectData} from "../../../../../types/records-table.types";
import {MoveDirection} from "../../../../../types/move-direction.types";

@Component({
  selector: 'app-single-select-table-item',
  standalone: false,
  templateUrl: './single-select-table-item.component.html',
  styleUrls: [
    '../../base-record-table-item-styles.scss',
    './single-select-table-item.component.scss'
  ]
})
export class SingleSelectTableItemComponent extends BaseRecordTableItemComponent implements OnInit {
  @ViewChild('matSelect') matSelect!: MatSelect;
  @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>;
  @ViewChildren(MatOption) matOptions!: QueryList<MatOption>;

  public loadingService: LoadingService = inject(LoadingService);
  public changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private utilityService: UtilityService = inject(UtilityService);
  private selectRecordTableItemService: SelectRecordTableItemService = inject(SelectRecordTableItemService);

  public isLoading: boolean = false;
  public selectData: Array<SelectData> | null = null;
  public searchTerm: string = '';
  public selectedIndex: number = 0;
  public isEmptyOptions: boolean = false;

  public ngOnInit(): void {
    this.initSubscriptions();
    this.initializeSelectOptions();
  }

  public filteredOptions(): Array<SelectData> {
    if (!this.selectData?.length) { return []; }

    const filteredOptions = this.selectData.filter((option: SelectData) => {
      return option.display && option.value.toLowerCase().includes(this.searchTerm.toLowerCase().trim());
    });

    this.isEmptyOptions = !filteredOptions.length;
    return filteredOptions;
  }

  public override getStringValue(): string {
    return this.getSelectedData()?.value ?? '';
  }

  protected handleSelectChange(event: MatSelectChange): void {
    if (event.value === 0) { event.value = null; }
    super.handleValueChange(event.value);
    this.searchTerm = '';
    if (this.record) {
      this.matSelect.value = this.value;
    }
  }

  public getSelectedData(): SelectData | null {
    return this.columnParam.metaSelectKeyList?.find((selectData: SelectData) =>
      selectData.id == this.value
    ) ?? null;
  }

  public isOptionShouldBeDisplayed(option: SelectData): boolean {
    return option.display || this.getSelectedData()?.id === option.id;
  }

  public getSelectedDataColor(): string | null {
    const selectedData = this.getSelectedData();
    return !selectedData?.color ? null : this.utilityService.hexToRgba(selectedData.color, 0.35);
  }

  public getColorWithOpacity(hex: string | null, opacity: number): string {
    return !hex ? '#fff' : this.utilityService.hexToRgba(hex, opacity);
  }

  public getScalingFactor(currentTableZoom: number): number {
    return currentTableZoom > 2 ? currentTableZoom/100 : currentTableZoom;
  }

  public async handleSelectClick(): Promise<void> {
    this.selectData && this.matSelect.open();
    this.columnParam.columnProperties.LAZY_SELECT && await this.loadSelectData();
  }

  public clearSearchTerm(): void {
    this.searchTerm = '';
  }

  public initializeSelectOptions(): void {
    if (this.columnParam.columnProperties.LAZY_SELECT) { return; }

    this.selectData = this.selectRecordTableItemService.initializeSelectOptions(this.columnParam);
  }

  public onSearchTermChange(newValue: string): void {
    this.searchTerm = newValue.trim();

    if (newValue) {
      this.value = null;
    }
  }

  public getPlaceholder(): string {
    return this.getStringValue();
  }

  public onArrowKey(event: Event, direction: MoveDirection): void {
    event.preventDefault();
    const step = direction === 'up' ? -1 : 1;
    this.moveSelection(step);
  }

  public onEnter(event: Event): void {
    event.preventDefault();
    if (!this.selectedIndex) { this.value = null; }

    const optionId = this.filteredOptions().find(i => i.id === this.selectedIndex)?.id;
    !this.selectRecordTableItemService.isMultiSelect(this.columnParam) ?
      this.handleSingleSelectEnter(optionId) :
      this.handleMultiSelectEnter(optionId);

    this.handleSelectChange({ value: this.value } as MatSelectChange);
  }

  private initSubscriptions(): void {
    this.dataSubscriptions.add(
      this.loadingService.loadingState$.subscribe({
        next: (isLoading: boolean) => this.setIsLoading(isLoading)
      })
    );
  }

  private setIsLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
  }

  private async loadSelectData(): Promise<void> {
    this.loadingService.setIsLoadingState(true);
    this.selectData = await this.selectRecordTableItemService.getSelectData(this.columnParam, Number(this.record?.id)).catch(() => []);

    this.loadingService.setIsLoadingState(false);
    this.changeDetectorRef.detectChanges();
    this.matSelect.open();
  }

  private handleSingleSelectEnter(optionId?: string | number): void {
    this.value = optionId;
    this.matSelect.close();
    this.inputElement.nativeElement.blur();
  }

  private handleMultiSelectEnter(optionId?: string | number): void {
    const currentValue = Array.isArray(this.value) ? [...this.value] : [];
    const existingIndex = currentValue.findIndex(v => v === optionId);
    existingIndex >= 0 ? currentValue.splice(existingIndex, 1) : currentValue.push(optionId);
    this.value = currentValue;
    this.searchTerm = '';
    this.changeDetectorRef.detectChanges();
  }

  private moveSelection(step: number): void {
    const options = this.filteredOptions();
    if (!options.length) { return; }

    const currentIndex = options.findIndex(option => option.id === this.selectedIndex);
    const newIndex = currentIndex === -1 ?
      options.length - 1 :
      (currentIndex + step + options.length) % options.length;

    this.selectedIndex = options[newIndex].id;
    this.selectRecordTableItemService.scrollSelectedOptionIntoView(newIndex, this.matOptions || []);
  }
}
